import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckIcon } from "@heroicons/react/24/outline";

import { Gradient } from "whatamesh";

import Layout from "../components/Layout";
import Seo from "../components/Seo";
import Features from "../components/Features";
import Cta from "../components/Cta";

const tiers = [
  {
    name: "The Essentials Plan",
    id: "tier-hobby",
    href: "/contact",
    priceMonthly: "$2,000",
    description:
      "For businesses ready for a professional, high-performing website, my lump sum option delivers value through:",
    features: [
      "Custom 5-page website tailored to your business.",
      "Hand-coded for performance, security, and scalability.",
      "Hosting: $25/mo",
      "Edits beyond CMS: $75/hr (1-hour minimum)",
    ],
    addOns: [
      "CMS for seamless content updates",
      "Advanced onsite SEO for better visibility",
      "Professional copywriting to engage your audience",
      "Advanced integrations like scheduling apps",
    ],
    featured: false,
  },
  {
    name: "The Ongoing Plan",
    id: "tier-enterprise",
    href: "/contact",
    priceMonthly: "$150",
    description:
      "Ideal for business owners who want ongoing support and complete peace of mind, this plan offers:",
    features: [
      "Custom 5-page website tailored to your business.",
      "Hand-coded for performance, security, and scalability.",
      "Hosting included",
      "Unlimited edits and updates",
      "Comprehensive support whenever you need it",
      "6-month minimum commitment, then cancel anytime",
      "Advanced onsite SEO for better visibility",
      "Privacy-forward analytics powered by Plausible",
      "Referral Discount: Drops to $125/month with a referral",
      "Ongoing partnership for peace of mind and ROI.",
    ],
    addOns: [],
    featured: true,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const PricingPage = () => {
  return (
    <Layout>
      <Seo title="Pricing" noindex={true} />
      <ServicesSection />
      <Pricing />
      <Features />
      <Cta />
    </Layout>
  );
};

const ServicesSection = () => {
  useEffect(() => {
    const gradient = new Gradient();
    gradient.initGradient("#gradient-canvas-cta");
  }, []);
  return (
    <div className="overflow-hidden relative">
      <canvas
        id="gradient-canvas-cta"
        data-transition-in
        className="absolute inset-0 opacity-30 z-0"
      />
      <div className="max-w-5xl mx-auto mt-20 md:mt-32 py-36 px-4 md:py-20 ">
        <div className="relative mx-auto max-w-5xl">
          <p className="mb-4 text-center subtitle">
            Transparent pricing. No surprises
          </p>
          <div className="flex justify-center text-center items-center px-4">
            <h1 className="mx-auto font-normal">Custom Website Pricing</h1>
          </div>

          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: 100, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="my-5 mx-auto h-1 w-20 rounded bg-brand"
          ></motion.div>
          <div className="w-full flex flex-col md:flex-row items-center justify-center gap-x-12 mt-4">
            <div className="w-full mx-auto text-center">
              <p className="text-center max-w-prose mx-auto">
                Your website should work for you, not against you. Whether you
                need a one-time build or ongoing support, my pricing is simple,
                upfront, and designed to deliver real value. No hidden fees—just
                a custom website that fits your business needs.
              </p>
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                whileInView={{ width: 100, opacity: 1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
                className="my-5 mx-auto h-1 w-20 rounded bg-brand-light"
              ></motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Pricing = () => {
  return (
    <div className="relative isolate bg-white px-6 py-24 sm:py-24 lg:px-8">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 -bottom-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl"
      >
        <div
          style={{
            clipPath:
              "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          }}
          className="mx-auto aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-[#27b6ed] to-[#9089fc] opacity-30"
        />
      </div>
      <div className="mx-auto max-w-4xl text-center">
        <p className="subtitle">Pricing</p>
        <h2 className="mt-2 text-balance mx-auto">
          Pricing Plans Designed for Your Needs
        </h2>
      </div>
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        whileInView={{ width: 100, opacity: 1 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className="my-5 mx-auto h-1 w-20 rounded bg-brand"
      ></motion.div>
      <p className="text-center max-w-prose mx-auto">
        Choose the plan that works best for your business. Whether you need a
        one-time solution to launch your site or ongoing support to keep things
        running smoothly, I’ve got you covered.
      </p>

      <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-y-6 sm:mt-12 sm:gap-y-0 lg:max-w-4xl lg:grid-cols-2">
        {tiers.map((tier, tierIdx) => (
          <div
            key={tier.id}
            className={classNames(
              tier.featured
                ? "relative bg-brand-bg shadow-2xl"
                : "bg-white/60 sm:mx-8 lg:mx-0",
              tier.featured
                ? ""
                : tierIdx === 0
                ? "rounded-t-3xl sm:rounded-b-none lg:rounded-bl-3xl lg:rounded-tr-none"
                : "sm:rounded-t-none lg:rounded-bl-none lg:rounded-tr-3xl",
              "rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10"
            )}
          >
            <h3
              id={tier.id}
              className={classNames(
                tier.featured ? "text-brand" : "text-brand",
                "text-base/7 font-semibold"
              )}
            >
              {tier.name}
            </h3>
            <p
              className={classNames(
                tier.featured ? "hidden" : "text-brand-bg/80",
                "text-xs -mb-3 mt-4 subtitle"
              )}
            >
              Starting at
            </p>
            <p className="mt-4 flex items-baseline gap-x-2">
              <span
                className={classNames(
                  tier.featured ? "text-white" : "text-brand-bg",
                  "font-montreal-bold  text-5xl max-w-2xl"
                )}
              >
                {tier.priceMonthly}
              </span>
              <span
                className={classNames(
                  tier.featured ? "text-gray-400" : "hidden text-gray-500",
                  "text-base"
                )}
              >
                /month
              </span>
            </p>
            <p
              className={classNames(
                tier.featured ? "text-gray-200" : "text-gray-600",
                "mt-6 text-base/7"
              )}
            >
              {tier.description}
            </p>
            <ul
              className={classNames(
                tier.featured ? "text-gray-200" : "text-gray-600",
                "mt-8 space-y-3 text-sm/6 sm:mt-10"
              )}
            >
              {tier.features.map((feature) => (
                <li key={feature} className="flex gap-x-3">
                  <CheckIcon
                    aria-hidden="true"
                    className={classNames(
                      tier.featured ? "text-brand" : "text-brand",
                      "h-6 w-5 flex-none"
                    )}
                  />
                  {feature}
                </li>
              ))}
              {tier.addOns.length > 0 && (
                <li className="mt-4">
                  <span className="font-semibold">Add-ons:</span>
                  <ul className="ml-6 space-y-2">
                    {tier.addOns.map((addOn) => (
                      <li key={addOn} className="flex gap-x-3">
                        <CheckIcon
                          aria-hidden="true"
                          className={classNames(
                            tier.featured ? "text-brand" : "text-brand",
                            "h-6 w-5 flex-none"
                          )}
                        />
                        {addOn}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
            <a
              href={tier.href}
              aria-describedby={tier.id}
              className={classNames(
                tier.featured
                  ? "button w-full"
                  : "button w-full focus-visible:outline-indigo-600",
                "mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10"
              )}
            >
              Get started today
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingPage;
